export function isNumeric(str: string): boolean {
  return !isNaN(Number(str)) && !isNaN(parseFloat(str));
}

export function ellipsizeText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }

  const ellipsis = "...";
  const halfMaxLength = Math.floor((maxLength - ellipsis.length) / 2);
  const start = text.slice(0, halfMaxLength);
  const end = text.slice(text.length - halfMaxLength);

  return `${start}${ellipsis}${end}`;
}

export function getFileName(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) {
    return fileName;
  } else {
    return fileName.substring(0, dotIndex);
  }
}

export function getFileExt(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) {
    return "";
  } else {
    return fileName.substring(dotIndex + 1);
  }
}

export function isInvalidDiscountAndCredit(
  discount: number,
  creditNotePercentage: number
): boolean {
  if (
    typeof discount === "number" &&
    !isNaN(discount) &&
    discount >= 0 &&
    discount <= 100 &&
    typeof creditNotePercentage === "number" &&
    !isNaN(creditNotePercentage) &&
    creditNotePercentage >= 0
  ) {
    return false;
  }
  return true;
}

export function buildAddress({
  firstLine,
  secondLine,
  city,
  state,
  pincode,
}: {
  firstLine: string;
  secondLine?: string;
  city: string;
  state: string;
  pincode: string;
}) {
  let addressLine = "";
  if (
    firstLine &&
    firstLine.trim() !== "" &&
    secondLine &&
    secondLine.trim() !== ""
  ) {
    addressLine = `${firstLine} ${secondLine}`;
  } else if (firstLine) {
    addressLine = firstLine;
  } else if (secondLine) {
    addressLine = secondLine;
  }
  const parts = [addressLine, city, state, pincode];
  const nonEmptyParts = parts.filter((part) => part && part.trim() !== "");
  return nonEmptyParts.join(", ");
}

export const computeDelayFromNow = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const diffMs = Date.now() - parsed.getTime();
  if (diffMs < 0) return "Scheduled";

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return "Just now";

  const days = Math.floor(diffMinutes / (60 * 24));
  const hours = Math.floor((diffMinutes % (60 * 24)) / 60);
  const minutes = diffMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(" ");
};

export const sanitizePhoneNumber = (input?: string | null) => {
  if (!input) return "";
  return input.replace(/[^0-9+]/g, "");
};

/** Clipboard: 10-digit national number (drops leading 91 when present). */
export const getNationalMobileForCopy = (input?: string | null): string => {
  if (!input) return "";

  // Remove all non-digit characters
  let digits = String(input).replace(/\D/g, "");

  // Remove country code (India: 91) if present
  if (digits.startsWith("91") && digits.length > 10) {
    digits = digits.slice(2);
  }

  // Always return last 10 digits
  return digits.slice(-10);
};

/** API / deep-link key: 91XXXXXXXXXX (null when a valid 10-digit national number is unavailable). */
export const toIndiaMobileWithCountryCode = (
  input?: string | null,
): string | null => {
  const national = getNationalMobileForCopy(input);
  if (national.length !== 10) return null;
  return `91${national}`;
};

/** Display-only: 10-digit national number without country code. */
export const formatMobileNumber = (input?: string | null) => {
  const national = getNationalMobileForCopy(input);
  if (national.length === 10) {
    return national;
  }

  return sanitizePhoneNumber(input) || "";
};

/**
 * Safely parse a mobile-config JSON value.
 * `JSON.parse(undefined)` throws `SyntaxError: "undefined" is not valid JSON`
 * because undefined is coerced to the string "undefined".
 */
export function parseJsonConfig<T>(raw: unknown, fallback: T): T {
  if (raw == null || raw === "") return fallback;
  if (typeof raw !== "string") return raw as T;
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") {
    return fallback;
  }
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return fallback;
  }
}

export const parseConfigArray = (raw: unknown, fallbackSplit = false): string[] => {
  if (Array.isArray(raw)) return raw.map(s => String(s).trim()).filter(Boolean);
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((s: unknown) => String(s).trim()).filter(Boolean);
    }
  } catch {
    // ignore parsing errors
  }
  if (fallbackSplit) {
    return raw.split(",").map((s: string) => s.trim()).filter(Boolean);
  }
  return [];
};
